import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import {
  Palmtree, Check, X, Trash2, Plus, ChevronLeft, ChevronRight, CalendarDays, List
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";

const API = "/api";

const STATUS_CONFIG = {
  geplant: { label: "Geplant", color: "bg-amber-100 text-amber-800 border-amber-200" },
  genehmigt: { label: "Genehmigt", color: "bg-green-100 text-green-800 border-green-200" },
  abgelehnt: { label: "Abgelehnt", color: "bg-red-100 text-red-800 border-red-200" },
};

const MONTH_NAMES = ["Januar", "Februar", "Maerz", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

export default function Urlaubsverwaltung() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "projektleiter" || user?.permissions?.can_manage_users;

  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [view, setView] = useState("calendar"); // "list" | "calendar"

  // Calendar state
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calData, setCalData] = useState([]);
  const [calLoading, setCalLoading] = useState(false);

  // New request dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newReq, setNewReq] = useState({ employee_id: "", from_date: "", to_date: "" });
  const [saving, setSaving] = useState(false);
  const [workdayCount, setWorkdayCount] = useState(null);

  useEffect(() => { loadData(); }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reqRes, empRes] = await Promise.all([
        authFetch(`${API}/vacation/requests?year=${year}`),
        isAdmin ? authFetch(`${API}/users`) : Promise.resolve(null),
      ]);
      if (reqRes.ok) setRequests(await reqRes.json());
      if (empRes?.ok) setEmployees(await empRes.json());
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  // Calendar data
  const loadCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const res = await authFetch(`${API}/vacation/calendar/${calYear}/${calMonth}`);
      if (res.ok) setCalData(await res.json());
    } catch {
      toast.error("Fehler beim Laden der Kalenderdaten");
    } finally {
      setCalLoading(false);
    }
  }, [calYear, calMonth]);

  useEffect(() => { if (view === "calendar") loadCalendar(); }, [view, loadCalendar]);

  // Calculate workdays when dates change
  useEffect(() => {
    if (newReq.from_date && newReq.to_date) {
      try {
        const from = parseISO(newReq.from_date);
        const to = parseISO(newReq.to_date);
        if (to >= from) {
          let count = 0;
          const d = new Date(from);
          while (d <= to) {
            if (d.getDay() !== 0 && d.getDay() !== 6) count++;
            d.setDate(d.getDate() + 1);
          }
          setWorkdayCount(count);
        } else {
          setWorkdayCount(null);
        }
      } catch {
        setWorkdayCount(null);
      }
    } else {
      setWorkdayCount(null);
    }
  }, [newReq.from_date, newReq.to_date]);

  const handleCreateRequest = async () => {
    const employeeId = isAdmin ? newReq.employee_id : user?.id;
    if (!employeeId || !newReq.from_date || !newReq.to_date) {
      toast.error("Bitte alle Felder ausfuellen");
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/vacation/plan`, {
        method: "POST",
        body: JSON.stringify({
          employee_id: employeeId,
          from_date: newReq.from_date,
          to_date: newReq.to_date,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Urlaubsantrag erstellt");
        if (data.exceeds_entitlement) {
          toast.warning("Achtung: Urlaubsanspruch wird ueberschritten!");
        }
        setShowNewDialog(false);
        setNewReq({ employee_id: "", from_date: "", to_date: "" });
        loadData();
        if (view === "calendar") loadCalendar();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Erstellen");
      }
    } catch {
      toast.error("Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await authFetch(`${API}/vacation/requests/${id}/approve`, { method: "PUT" });
      if (res.ok) {
        toast.success("Urlaub genehmigt");
        loadData();
        if (view === "calendar") loadCalendar();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler beim Genehmigen");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await authFetch(`${API}/vacation/requests/${id}/reject`, { method: "PUT" });
      if (res.ok) {
        toast.success("Urlaub abgelehnt");
        loadData();
        if (view === "calendar") loadCalendar();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler beim Ablehnen");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`${API}/vacation/requests/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Urlaubsantrag geloescht");
        loadData();
        if (view === "calendar") loadCalendar();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler beim Loeschen");
    }
  };

  const filteredRequests = useMemo(() => {
    let result = [...requests];
    if (statusFilter !== "all") result = result.filter(r => r.status === statusFilter);
    if (employeeFilter !== "all") result = result.filter(r => r.employee_id === employeeFilter);
    return result;
  }, [requests, statusFilter, employeeFilter]);

  const pendingCount = requests.filter(r => r.status === "geplant").length;

  const formatDate = (dateStr) => {
    try { return format(parseISO(dateStr), "dd.MM.yyyy", { locale: de }); }
    catch { return dateStr; }
  };

  const calPrev = () => {
    if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const calNext = () => {
    if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  return (
    <div className="space-y-5" data-testid="vacation-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-[20px] font-bold text-gray-900">Urlaubsverwaltung</h1>
          {pendingCount > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="pending-count">
              {pendingCount} offen
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-md p-0.5" data-testid="view-toggle">
            <button
              onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded transition-colors ${view === "calendar" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="view-calendar-btn"
            >
              <CalendarDays className="w-3.5 h-3.5" />Kalender
            </button>
            <button
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded transition-colors ${view === "list" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="view-list-btn"
            >
              <List className="w-3.5 h-3.5" />Liste
            </button>
          </div>
          <Button
            onClick={() => {
              setNewReq({ employee_id: isAdmin && employees.length ? employees[0].id : user?.id || "", from_date: "", to_date: "" });
              setShowNewDialog(true);
            }}
            className="bg-green-700 hover:bg-green-800 text-white"
            data-testid="new-vacation-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Urlaub beantragen
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {view === "calendar" && <CalendarView
        calData={calData}
        calMonth={calMonth}
        calYear={calYear}
        calLoading={calLoading}
        onPrev={calPrev}
        onNext={calNext}
        isAdmin={isAdmin}
      />}

      {/* List View */}
      {view === "list" && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-1">
              <button onClick={() => setYear(y => y - 1)} className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-900" data-testid="prev-year-btn">&lt;</button>
              <span className="px-2 py-1.5 text-sm font-semibold text-gray-900" data-testid="year-display">{year}</span>
              <button onClick={() => setYear(y => y + 1)} className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-900" data-testid="next-year-btn">&gt;</button>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white" data-testid="status-filter">
              <option value="all">Alle Status</option>
              <option value="geplant">Geplant</option>
              <option value="genehmigt">Genehmigt</option>
              <option value="abgelehnt">Abgelehnt</option>
            </select>
            {isAdmin && (
              <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white" data-testid="employee-filter">
                <option value="all">Alle Mitarbeiter</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            )}
            <span className="text-sm text-gray-500">{filteredRequests.length} Antraege</span>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="requests-table">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {isAdmin && <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Mitarbeiter</th>}
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Von</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Bis</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Tage</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Status</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Erstellt</th>
                  <th className="text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-gray-400 text-sm">Lade Daten...</td></tr>
                ) : filteredRequests.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-gray-400 text-sm">Keine Urlaubsantraege vorhanden</td></tr>
                ) : (
                  filteredRequests.map((req) => {
                    const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.geplant;
                    return (
                      <tr key={req.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`vacation-row-${req.id}`}>
                        {isAdmin && <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{req.employee_name || "\u2014"}</td>}
                        <td className="px-4 py-2.5 text-sm text-gray-700">{formatDate(req.from_date)}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-700">{formatDate(req.to_date)}</td>
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{req.days}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border ${sc.color}`} data-testid={`status-${req.id}`}>{sc.label}</span>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-500">{formatDate(req.created_at?.split("T")[0] || req.created_at)}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {isAdmin && req.status === "geplant" && (
                              <>
                                <button onClick={() => handleApprove(req.id)} className="p-1.5 rounded hover:bg-green-50 text-green-700 transition-colors" title="Genehmigen" data-testid={`approve-${req.id}`}><Check className="w-4 h-4" /></button>
                                <button onClick={() => handleReject(req.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors" title="Ablehnen" data-testid={`reject-${req.id}`}><X className="w-4 h-4" /></button>
                              </>
                            )}
                            {(isAdmin || (req.employee_id === user?.id && req.status !== "genehmigt")) && (
                              <button onClick={() => handleDelete(req.id)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors" title="Loeschen" data-testid={`delete-${req.id}`}><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Summary cards for admins */}
      {isAdmin && !loading && (
        <div className="grid grid-cols-3 gap-4" data-testid="vacation-summary">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <p className="text-[11px] font-medium text-amber-600 uppercase tracking-wider">Offen</p>
            <p className="text-2xl font-bold text-amber-800 mt-1">{requests.filter(r => r.status === "geplant").length}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-[11px] font-medium text-green-600 uppercase tracking-wider">Genehmigt</p>
            <p className="text-2xl font-bold text-green-800 mt-1">{requests.filter(r => r.status === "genehmigt").length}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-[11px] font-medium text-red-500 uppercase tracking-wider">Abgelehnt</p>
            <p className="text-2xl font-bold text-red-800 mt-1">{requests.filter(r => r.status === "abgelehnt").length}</p>
          </div>
        </div>
      )}

      {/* New Request Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-600" />
              Urlaub beantragen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isAdmin && (
              <div>
                <Label>Mitarbeiter</Label>
                <select value={newReq.employee_id} onChange={(e) => setNewReq({ ...newReq, employee_id: e.target.value })} className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-md" data-testid="select-employee">
                  <option value="">-- Mitarbeiter waehlen --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Von</Label>
                <Input type="date" value={newReq.from_date} onChange={(e) => setNewReq({ ...newReq, from_date: e.target.value })} data-testid="vacation-from-date" />
              </div>
              <div>
                <Label>Bis</Label>
                <Input type="date" value={newReq.to_date} onChange={(e) => setNewReq({ ...newReq, to_date: e.target.value })} data-testid="vacation-to-date" />
              </div>
            </div>
            {workdayCount !== null && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-center" data-testid="workday-count">
                <span className="text-sm text-blue-700">
                  <span className="font-bold text-lg">{workdayCount}</span> Arbeitstag{workdayCount !== 1 ? "e" : ""}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)} data-testid="cancel-vacation-btn">Abbrechen</Button>
            <Button
              onClick={handleCreateRequest}
              disabled={saving || !newReq.from_date || !newReq.to_date || (isAdmin && !newReq.employee_id)}
              className="bg-green-700 hover:bg-green-800"
              data-testid="submit-vacation-btn"
            >
              {saving ? "Speichern..." : "Urlaub beantragen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========= Team Calendar Component ========= */
function CalendarView({ calData, calMonth, calYear, calLoading, onPrev, onNext, isAdmin }) {
  // Build days in month
  const daysInMonth = new Date(calYear, calMonth, 0).getDate();
  const firstDow = new Date(calYear, calMonth - 1, 1).getDay(); // 0=Sun

  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const dayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  // Build lookup: { employee_id: { "2026-03-15": "genehmigt" } }
  const lookup = useMemo(() => {
    const m = {};
    for (const emp of calData) {
      const days = {};
      for (const d of emp.days) {
        days[d.date] = d.status;
      }
      m[emp.employee_id] = { name: emp.employee_name, days };
    }
    return m;
  }, [calData]);

  const employees = useMemo(() => {
    if (!calData.length) return [];
    return calData.map(e => ({ id: e.employee_id, name: e.employee_name }));
  }, [calData]);

  const isWeekend = (day) => {
    const d = new Date(calYear, calMonth - 1, day);
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const getDow = (day) => {
    const d = new Date(calYear, calMonth - 1, day);
    const dow = d.getDay();
    return dow === 0 ? 6 : dow - 1; // Convert to Mon=0
  };

  const today = new Date();
  const isToday = (day) => today.getFullYear() === calYear && today.getMonth() + 1 === calMonth && today.getDate() === day;

  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="vacation-calendar">
      {/* Calendar header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <button onClick={onPrev} className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-gray-600" /></button>
        <span className="text-[14px] font-bold text-gray-900" data-testid="cal-month-display">{MONTH_NAMES[calMonth - 1]} {calYear}</span>
        <button onClick={onNext} className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-gray-600" /></button>
      </div>

      {calLoading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Lade Kalenderdaten...</div>
      ) : employees.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">Kein Urlaub in diesem Monat geplant</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: daysInMonth * 28 + 160 }}>
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[150px]">Mitarbeiter</th>
                {dayNumbers.map(day => {
                  const we = isWeekend(day);
                  const td = isToday(day);
                  return (
                    <th
                      key={day}
                      className={`text-center text-[10px] font-medium px-0 py-1.5 min-w-[28px] ${we ? "bg-gray-100 text-gray-400" : td ? "bg-blue-50 text-blue-700 font-bold" : "text-gray-500"}`}
                      data-testid={`cal-day-header-${day}`}
                    >
                      <div className="leading-tight">{dayLabels[getDow(day)]}</div>
                      <div className={`leading-tight ${td ? "font-bold" : ""}`}>{day}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const empDays = lookup[emp.id]?.days || {};
                return (
                  <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50/30" data-testid={`cal-row-${emp.id}`}>
                    <td className="text-[12px] font-medium text-gray-800 px-3 py-2 sticky left-0 bg-white z-10 whitespace-nowrap border-r border-gray-100">{emp.name}</td>
                    {dayNumbers.map(day => {
                      const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const status = empDays[dateStr];
                      const we = isWeekend(day);
                      const td = isToday(day);

                      let cellBg = we ? "bg-gray-50" : "";
                      let content = null;
                      if (status === "genehmigt") {
                        cellBg = "bg-green-400";
                        content = <div className="w-full h-full rounded-sm" title="Genehmigt" />;
                      } else if (status === "geplant") {
                        cellBg = "bg-amber-300";
                        content = <div className="w-full h-full rounded-sm opacity-70" title="Geplant" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)" }} />;
                      }

                      return (
                        <td
                          key={day}
                          className={`text-center px-0.5 py-1.5 ${cellBg} ${td && !status ? "ring-1 ring-inset ring-blue-300" : ""}`}
                          data-testid={`cal-cell-${emp.id}-${day}`}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-5 px-4 py-2.5 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-3 rounded-sm bg-green-400" />
          <span className="text-[11px] text-gray-600">Genehmigt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-3 rounded-sm bg-amber-300" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)" }} />
          <span className="text-[11px] text-gray-600">Geplant</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-3 rounded-sm bg-gray-100 border border-gray-200" />
          <span className="text-[11px] text-gray-600">Wochenende</span>
        </div>
      </div>
    </div>
  );
}
